import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { IMapTemplate } from 'src/app/components/csv-mapper/csv-map.class';
import { AnnotationService } from 'src/app/services/annotation/annotation.service';
import { GenericTypeService } from 'src/app/services/generic-type/generic-type.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { LocationService } from 'src/app/services/location/location.service';
import { LocationStoreService } from 'src/app/stores/location-store/location-store.service';
import { GenericTypeStoreService } from 'src/app/stores/generic-types-store/generic-type-store.service';

@Component({
  selector: 'app-summary-modal',
  templateUrl: './summary-modal.component.html',
  styleUrls: ['./summary-modal.component.sass']
})
export class SummaryModalComponent implements OnInit, OnDestroy {

  unsub$: Subject<any> = new Subject<any>()

  @ViewChild('summaryModal') summaryModal: TemplateRef<any>

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private gTypeService: GenericTypeService,
    private modalService: BsModalService,
    private annotService: AnnotationService,
    private navService: NavigationService,
    private locationService: LocationService,
    private locationStore: LocationStoreService,
    private genericTypeStore: GenericTypeStoreService
  ) { }

  ui: {
    /** 
     * TO BE DEPRECATED
     * Used to determine whether the component comes with paramID or not 
     */
    _paramId?: string
    /** Filtered sub-location ID */
    filteredId?: string
    /** Filtered sub-location Name */
    filteredName?: string
    currLoc?: any
    isCharts?: any
    charts?: {
      assetSeverity?: {
        data: any[]
        mapping: IMapTemplate
      },
      severityCount?: {
        data: any[]
        mapping: IMapTemplate
      },
      defectTypes?: {
        data: any[]
        mapping: IMapTemplate
      }
    },
    _cache?: {
      severityTypes?: any
      annotationTypes?: any
    },
    /** Turns true when the same asset is clicked and is currently shown */
    currSelected?: boolean
    /** Indicator that the button has been clicked */
    _getReportsClicked?: boolean
  } = {
      isCharts: true
    }

  /** Boolean to mark if the user is navigating to another location */
  toLocation: boolean = false

  /** Modal reference for manipulation */
  modalRef: BsModalRef

  ngOnInit(): void {
    this.ui.charts = this.ui.charts || {}
    this.ui._cache = this.ui._cache || {}
    this.navService.simulCloseOnInit('modal')

    this.ui.charts = {
      severityCount: {
        data: [],
        mapping: {
          name: 'Severity Count',
          id: 'severity-count',
          fields: [
            {
              text: 'Severities',
              placeholder: 'X-Axis',
              id: 'xaxis',
              key: 'name'
            },
            {
              text: 'Total',
              placeholder: 'Y-Axis',
              id: 'yaxis',
              key: 'count'
            },
            {
              text: 'Color',
              placeholder: 'Custom Color',
              id: 'custom-color',
              key: 'color'
            },
            {
              text: 'ID',
              placeholder: 'Data unique ID',
              id: 'data-id',
              key: 'id'
            }
          ]
        }
      },
      defectTypes: {
        data: [],
        mapping: {} as IMapTemplate
      },
      assetSeverity: {
        data: [],
        mapping: {
          name: 'Asset Severity',
          id: 'asset-severity',
          fields: [
            {
              text: 'Header Text',
              placeholder: 'Header text name',
              id: 'header-text',
              key: 'locationName'
            },
            {
              text: 'Header ID',
              placeholder: 'ID for header',
              id: 'header-id',
              key: 'locationId'
            },
            {
              text: 'Color Name',
              placeholder: 'Key for the color name',
              id: 'color-name',
              key: 'name'
            },
            {
              text: 'Color Value',
              placeholder: 'Key for the color value',
              id: 'color-value',
              key: 'color'
            },
            {
              text: 'Color ID',
              placeholder: 'Key for the color id',
              id: 'color-id',
              key: 'id'
            },
            {
              text: 'Count',
              placeholder: 'Key for the number of colors',
              id: 'color-count',
              key: 'count'
            }
          ]
        }
      }
    }

    this.gTypeService.findAll().pipe(take(1)).subscribe((res) => {
      this.genericTypeStore.setGenericTypes = res.data;

      this.ui._cache.severityTypes = this.genericTypeStore.getGenericType('severity')
      this.ui._cache.annotationTypes = this.genericTypeStore.getGenericType('annotation')

      this.locationStore.currLocation$.pipe(takeUntil(this.unsub$.asObservable()))
        .subscribe((res) => {

          if (!('_id' in res) && !('id' in res)) return

          this.ui.currLoc = res
          /**
           * Loop through all the child to individually store their severity information
           */
          const loadChildSeverity = async () => {
            const children = this.ui?.currLoc?.children
              , _cache = this.ui?._cache

            this.ui.charts.assetSeverity.data = []

            await children.reduce(async (p, child: { id: string, name: string }, childIdx) => {
              if (!child.id) return p

              await p

              const param = [{
                key: `locations[0]`,
                val: child.id
              }]

              const res: { annotation_type: any[], location_component: any[], severity_type: any[] } = await this.annotService.findDefectCount(param).toPromise()
              const { severity_type: _severity_type, annotation_type: _annotation_type } = res

              if (_severity_type?.length) {

                const severity_type = _severity_type.reduce((p, severType) => {
                  p[severType._id] = severType.count
                  return p
                }, {})

                const severityInfo = Object.keys(_cache.severityTypes).reduce((p, severId, idx) => {
                  const severity = _cache.severityTypes[severId]
                  if (severity_type[severId]) {
                    p.push({
                      id: severId,
                      locationName: child.name,
                      locationId: child.id,
                      color: severity?.description,
                      name: severity?.name,
                      count: severity_type[severId] || 0
                    })
                    return p
                  }
                  return p
                }, [])

                const assetSeverity = this.ui.charts.assetSeverity
                assetSeverity.data = assetSeverity.data.concat(severityInfo)
              }

              return p
            }, Promise.resolve())
          }

          loadChildSeverity()

          if (this.ui.filteredId) this.loadAssetCharts(this.ui.filteredId)
        })

      this.route.params.pipe(takeUntil(this.unsub$.asObservable()))
        .subscribe((params) => {
          const { id } = params
          this.ui._paramId = id
          this.ui.filteredId = id

          /** Perform function to refresh the data */
          this.loadAssetCharts(this.ui.filteredId)

          if (!this.modalRef) {
            this.openModal()
          }
        })
    });


  }

  ngOnDestroy(): void {
    this.unsub$.next()
    this.unsub$.unsubscribe()
  }

  openModal() {

    this.modalRef = this.modalService.show(this.summaryModal, {
      class: 'modal-lg'
    })
    this.modalRef.onHide.pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe(evt => {
        if (!this.toLocation)
          this.navigateBack()
      })
  }

  navigateBack() {
    this.navService.simulateClose()
  }

  closeModal() {
    if (!this.modalRef) {
      throw new Error('Unable to close modal')
    }
    this.modalRef.hide()
  }


  loadAssetCharts(subLocId: string) {
    const params = [{
      key: `locations[0]`,
      val: subLocId
    }]
      , _cache = this.ui?._cache
      , children = this.ui.currLoc?.children

    if (!children?.length) return

    this.ui.filteredId = subLocId
    this.ui.filteredName = children.find(child => child.id === subLocId)?.name

    this.annotService.findDefectCount(params).pipe(take(1)).subscribe((res) => {

      const { severity_type, annotation_type } = res

      if (severity_type) {
        this.ui.charts.severityCount.data = Object.keys(_cache.severityTypes).map((id) => {
          const severity = _cache.severityTypes[id]
          return {
            id,
            color: severity?.description,
            name: severity?.name,
            count: severity_type.find(sc => sc._id === id)?.count || 0
          }
        })
      }

      if (annotation_type) {
        this.ui.charts.defectTypes.data = Object.keys(_cache.annotationTypes).map((id) => {
          const annotType = _cache.annotationTypes[id]

          return {
            id,
            name: annotType?.name,
            count: annotation_type.find(atc => atc._id === id)?.count || 0
          }
        })
      }
    })
  }

  onChartClicked(name: string, evt: any) {
    switch (name) {
      case 'assetSeverity': {
        /** Change the filter here */
        const origin = this.ui._paramId ? '..' : '.'

        this.ui.currSelected = this.ui.filteredId === evt?.header?.id
        if (this.ui.currSelected)
          setTimeout(() => {
            this.ui.currSelected = false
          }, 1000)

        this.router.navigate([`${origin}/${evt?.header?.id}`], { relativeTo: this.route })
        this.loadAssetCharts(evt?.header?.id)
        break
      }
    }
  }

  goToLocation(locId: string) {
    this.router.navigate([`/projects/${locId}`])
    this.toLocation = true
    this.closeModal()
  }

  /**
   * Similar method to @function summary.generateReport()
   */
  async generateReport(locId: string) {
    if (!locId) return

    await this.locationService.generateReport(locId)
  }

}

enum BSModalHideEvt {
  browserBack = 'browser-back-navigation-clicked',

}
